import { Pause, PlayArrow } from "@mui/icons-material";
import { Box, IconButton, Skeleton, Slider, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { MessageProps } from "../../../interfaces/all";
import { resolveMediaUrl } from "../../../utils/media";
import MessageAvatar from "./MessageAvatar";
import MessageMetaInfo from "./MessageMetaInfo";
import { MessageContainer, MessageContent } from "./styles";

interface VoiceMessagePropsSkeleton extends MessageProps {
  showSkeleton?: boolean;
}

const VoiceMessage: React.FC<VoiceMessagePropsSkeleton> = ({
  message,
  showAvatar = true,
  showDate = true,
  showSkeleton = false,
}) => {
  const isFromClient = message.sentBy === "client";
  const currentReadStatus = message.readStatus || { isRead: false };
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Extrair dados da mensagem de voz de forma segura
  const content = message.content as any;

  // Tentar encontrar os dados em vários lugares possíveis
  const voiceData =
    content.voiceData ||
    content.voice ||
    content.audio ||
    content.audioData ||
    {};
  const rawUrl =
    typeof voiceData === "string"
      ? voiceData
      : voiceData.url || voiceData.mediaUrl || voiceData.media_url || "";
  const url = resolveMediaUrl(rawUrl);

  // Simplificar o mimeType para evitar problemas com parâmetros extras (codecs)
  const rawMimeType = voiceData.mimeType || voiceData.mime_type || "audio/ogg";
  const mimeType = rawMimeType.split(";")[0].trim();

  const formatName = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const senderName = isFromClient
    ? message.user?.name || "Cliente"
    : "Atendente";

  const togglePlay = () => {
    if (!audioRef.current) return;

    if (isPlaying) {
      audioRef.current.pause();
    } else {
      // Garantir que a URL esteja carregada antes de dar play
      if (!audioRef.current.src || audioRef.current.src !== url) {
        // Se não tiver src na tag audio (estamos usando <source>), o browser deveria usar a primeira source válida.
        // Mas forçar o load pode ajudar.
        audioRef.current.load();
      }

      audioRef.current.play().catch((err) => {
        console.error("❌ [VoiceMessage] Falha ao iniciar reprodução:", {
          name: err.name,
          message: err.message,
          url: url,
          mimeType,
        });
      });
    }
  };

  const [duration, setDuration] = useState(voiceData.duration || 0);

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleAudioError = (e: any) => {
    const error = e.target.error;
    console.error("❌ [VoiceMessage] Erro crítico no elemento <audio>:", {
      code: error?.code,
      message: error?.message,
      url: url,
    });
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleSliderChange = (_: Event, value: number | number[]) => {
    if (!audioRef.current) return;
    const newTime = value as number;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const formatTime = (time: number) => {
    if (!time || isNaN(time)) return "00:00";
    return new Date(time * 1000).toISOString().substr(14, 5);
  };

  if (showSkeleton) {
    // ... skeleton remains the same ...
    return (
      <MessageContainer isFromClient={isFromClient} showTriangle={false}>
        <Box
          sx={{ position: "relative", display: "flex", alignItems: "center" }}
        >
          <MessageContent
            isFromClient={isFromClient}
            showTriangle={false}
            sx={{ pr: "62px", minWidth: 240 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                minWidth: 200,
              }}
            >
              <Skeleton variant="circular" width={32} height={32} />
              <Skeleton
                variant="rectangular"
                width={120}
                height={8}
                sx={{ borderRadius: 4 }}
              />
              <Skeleton variant="text" width={32} height={16} />
            </Box>
          </MessageContent>
        </Box>
      </MessageContainer>
    );
  }

  return (
    <MessageContainer isFromClient={isFromClient} showTriangle={showAvatar}>
      {/* Elemento de áudio oculto */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio
        ref={audioRef}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleAudioError}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        crossOrigin="anonymous"
        hidden
      >
        <source src={url} type={mimeType} />
      </audio>

      {isFromClient ? (
        <>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
          <MessageContent
            isFromClient={isFromClient}
            showTriangle={showAvatar}
            sx={{ pr: "62px", minWidth: 240 }}
          >
            {showAvatar && (
              <Box
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#075e54",
                  mb: 0.5,
                }}
              >
                {formatName(senderName)}
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                py: 0.5,
              }}
            >
              <IconButton
                onClick={togglePlay}
                size="small"
                sx={{
                  color: "inherit",
                  p: 0.5,
                }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                <Slider
                  value={currentTime}
                  max={duration || 0}
                  onChange={handleSliderChange}
                  size="small"
                  sx={{
                    color: "inherit",
                    "& .MuiSlider-thumb": {
                      width: 10,
                      height: 10,
                    },
                    "& .MuiSlider-track": {
                      height: 3,
                    },
                    "& .MuiSlider-rail": {
                      height: 3,
                      opacity: 0.3,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ minWidth: 35, fontSize: "0.7rem", ml: 0.5 }}
              >
                {isPlaying ? formatTime(currentTime) : formatTime(duration)}
              </Typography>
            </Box>
            <MessageMetaInfo
              message={message}
              showDate={showDate}
              isFromClient={isFromClient}
              readStatus={currentReadStatus}
            />
          </MessageContent>
        </>
      ) : (
        <>
          <MessageContent
            isFromClient={isFromClient}
            showTriangle={showAvatar}
            sx={{ pr: "62px", minWidth: 240 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                py: 0.5,
              }}
            >
              <IconButton
                onClick={togglePlay}
                size="small"
                sx={{
                  color: "inherit",
                  p: 0.5,
                }}
              >
                {isPlaying ? <Pause /> : <PlayArrow />}
              </IconButton>
              <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                <Slider
                  value={currentTime}
                  max={duration || 0}
                  onChange={handleSliderChange}
                  size="small"
                  sx={{
                    color: "inherit",
                    "& .MuiSlider-thumb": {
                      width: 10,
                      height: 10,
                    },
                    "& .MuiSlider-track": {
                      height: 3,
                    },
                    "& .MuiSlider-rail": {
                      height: 3,
                      opacity: 0.3,
                    },
                  }}
                />
              </Box>
              <Typography
                variant="caption"
                sx={{ minWidth: 35, fontSize: "0.7rem", ml: 0.5 }}
              >
                {isPlaying ? formatTime(currentTime) : formatTime(duration)}
              </Typography>
            </Box>
            <MessageMetaInfo
              message={message}
              showDate={showDate}
              isFromClient={isFromClient}
              readStatus={currentReadStatus}
            />
          </MessageContent>
          <MessageAvatar sentBy={message.sentBy} showAvatar={showAvatar} />
        </>
      )}
    </MessageContainer>
  );
};

export default VoiceMessage;
